import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { UserSessionStore } from '../../shared/services/user-session.store';
import { Announcement, ANNOUNCEMENT, ANNOUNCEMENT_STORAGE_PREFIX } from './announcement.config';

/** Days the card was closed with the cross (local YYYY-MM-DD), and whether the user clicked the call to action. */
export interface AnnouncementHistory {
  shows: string[];
  registered: boolean;
}

const OPEN_DELAY_MS = 3000;
const END_CHECK_MS = 30_000;
const MAX_SHOWS = 2;
const MIN_GAP_DAYS = 2;

export function getDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** A show is counted when the card is closed, not when it opens: reloading without closing shows it again. */
export function isAnnouncementDue(announcement: Announcement, history: AnnouncementHistory, now: Date): boolean {
  const today = getDayKey(now);
  if (now >= announcement.endsAt || history.registered || history.shows.includes(today)) return false;
  if (history.shows.length >= MAX_SHOWS) return false;
  const lastShow = history.shows.at(-1);
  // Day keys parse as UTC midnights, so the difference is a whole number of days whatever the DST.
  return !lastShow || (Date.parse(today) - Date.parse(lastShow)) / 86_400_000 >= MIN_GAP_DAYS;
}

/** Floating card announcing an event, bottom left next to the menu, on every page of a logged-in user. */
@Component({
  selector: 'app-announcement-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './announcement-card.component.html',
  styles: `
    @keyframes announcement-in {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
    }
  `,
})
export class AnnouncementCardComponent implements OnInit, OnDestroy {
  private readonly sessionStore = inject(UserSessionStore);
  private openTimer?: ReturnType<typeof setTimeout>;
  private endCheck?: ReturnType<typeof setInterval>;

  readonly announcement = ANNOUNCEMENT;
  readonly isOpen = signal(false);

  ngOnInit(): void {
    if (this.isDue()) this.openTimer = setTimeout(() => this.open(), OPEN_DELAY_MS);
  }

  ngOnDestroy(): void {
    clearTimeout(this.openTimer);
    clearInterval(this.endCheck);
  }

  close(): void {
    const today = getDayKey(new Date());
    this.updateHistory(history => ({
      ...history,
      shows: history.shows.includes(today) ? history.shows : [...history.shows, today],
    }));
    this.hide();
  }

  register(): void {
    this.updateHistory(history => ({ ...history, registered: true }));
    this.hide();
  }

  private open(): void {
    // Checked again: the card may have been closed in another tab, or the event started, during the delay.
    if (!this.isDue()) return;
    this.isOpen.set(true);
    this.endCheck = setInterval(() => {
      if (this.announcement && new Date() >= this.announcement.endsAt) this.hide();
    }, END_CHECK_MS);
  }

  private hide(): void {
    this.isOpen.set(false);
    clearInterval(this.endCheck);
  }

  private isDue(): boolean {
    const history = this.getHistory();
    return !!this.announcement && !!history && isAnnouncementDue(this.announcement, history, new Date());
  }

  private getStorageKey(): string | null {
    const userId = this.sessionStore.userSession()?.id;
    if (!this.announcement || userId === undefined || userId < 0) return null;
    return `${ANNOUNCEMENT_STORAGE_PREFIX}${this.announcement.id}:${userId}`;
  }

  /** Null when the storage is unavailable or unreadable: better never shown than shown on every page. */
  private getHistory(): AnnouncementHistory | null {
    const key = this.getStorageKey();
    if (!key) return null;
    try {
      const stored = localStorage.getItem(key);
      return stored ? (JSON.parse(stored) as AnnouncementHistory) : { shows: [], registered: false };
    } catch {
      return null;
    }
  }

  private updateHistory(update: (history: AnnouncementHistory) => AnnouncementHistory): void {
    const key = this.getStorageKey();
    const history = this.getHistory();
    if (!key || !history) return;
    try {
      localStorage.setItem(key, JSON.stringify(update(history)));
    } catch {
      // Storage full or blocked: the card still closes for this page load.
    }
  }
}
